import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { extractH264Annexb } from '../../utils/nativeFrameEnvelope';

/**
 * NativeLivePlayer — Phase 4b replacement for ``<WHEPPlayer>``.
 *
 * Pipeline:
 *   MD300 ── TCP :34006 ──► backend media_listener ──► WebSocket ──► JMuxer ──► <video>
 *
 * The browser receives raw H.264 Annex-B bytes (one Meitrack-fragment payload
 * per WS frame, header already stripped). JMuxer transmuxes those into
 * fragmented MP4 in real-time and feeds them through MediaSource Extensions
 * to the native ``<video>`` tag — no MediaMTX, no RTMP, no FFmpeg, no
 * server-side transcode.
 *
 * JMuxer is loaded via ``<script src="/jmuxer.min.js">`` in
 * ``public/index.html`` (vendored UMD bundle); accessed as ``window.JMuxer``.
 * We did NOT use the npm package because its UMD wrapper requires Node's
 * ``stream`` module which webpack 5 (CRA default) doesn't polyfill.
 *
 * Reliability features:
 *   • The listener primes us with the most recent SPS/PPS/IDR so playback
 *     starts within ~1 keyframe interval (≤2 s) regardless of how late we
 *     attach to the device's stream.
 *   • JMuxer auto-resyncs at the next IDR if the MSE buffer underflows
 *     (e.g. cellular blip) — no manual stall recovery needed.
 *   • On WebSocket close we drop the player into a "Reconnecting…" state
 *     and back off exponentially (1 s → 2 s → 4 s → max 15 s) until the
 *     listener reports an active session for our IMEI.
 *   • iter150.3 — capped at MAX_RECONNECT_ATTEMPTS so the player surfaces
 *     a clear "Stream lost" state instead of flashing forever when the
 *     device-side TCP keeps dropping.
 */
const MAX_RECONNECT_DELAY_MS = 15000;
const MAX_RECONNECT_ATTEMPTS = 5;

const NativeLivePlayer = React.forwardRef(function NativeLivePlayer(
  { wsUrl, wsUrlFactory, onStatusChange, label },
  ref,
) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const muxerRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectDelayRef = useRef(1000);
  const reconnectAttemptsRef = useRef(0);
  const closedByUnmountRef = useRef(false);

  const [status, setStatus] = useState('connecting');

  // iter150.6 — `?debug=1` URL flag enables an in-tile diagnostic
  // overlay so we can see EXACTLY why the "Connecting…" overlay is
  // sticking, what the WS is doing, and what the <video> element
  // thinks of its own state. Production users won't see this.
  // iter151 — keep it strictly URL-gated. `?debug=1` enables it for the
  // current page load only; no persistence, so normal users
  // never see an overlay from an old troubleshooting session.
  const debugMode = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('debug') === '1';
    } catch (_) { return false; }
  }, []);
  const debugCountersRef = useRef({ wsFrames: 0, wsBytes: 0, events: [] });
  const [debugSnapshot, setDebugSnapshot] = useState(null);

  const recordDebugEvent = useCallback((name) => {
    if (!debugMode) return;
    const list = debugCountersRef.current.events;
    list.push(`${new Date().toISOString().slice(11, 23)} ${name}`);
    while (list.length > 8) list.shift();
  }, [debugMode]);

  const updateStatus = useCallback((s) => {
    setStatus(s);
    if (onStatusChange) onStatusChange(s);
  }, [onStatusChange]);

  const teardown = useCallback(() => {
    try { wsRef.current?.close(); } catch (_) { /* noop */ }
    wsRef.current = null;
    try { muxerRef.current?.destroy?.(); } catch (_) { /* noop */ }
    muxerRef.current = null;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const connect = useCallback(async () => {
    if (!videoRef.current) return;
    if (!wsUrl && !wsUrlFactory) return;
    teardown();

    // JMuxer is loaded as a UMD bundle in public/index.html — it attaches
    // to window.JMuxer. Guard against the rare case the script tag hasn't
    // finished loading yet (e.g. very fast nav into the page).
    const JMuxerCtor = typeof window !== 'undefined' ? window.JMuxer : null;
    if (typeof JMuxerCtor !== 'function') {

      console.warn('[NativeLivePlayer] window.JMuxer not available yet — retrying in 250ms');
      reconnectTimerRef.current = setTimeout(connect, 250);
      return;
    }

    // Resolve the WebSocket URL. wsUrlFactory (async) is used by the live tile
    // so it can mint a short-lived stream token per (re)connection. Falls back
    // to a static wsUrl when no factory is supplied.
    let targetUrl = wsUrl;
    if (wsUrlFactory) {
      try {
        targetUrl = await wsUrlFactory();
      } catch (_) {
        scheduleReconnect();
        return;
      }
    }
    if (!targetUrl) {
      scheduleReconnect();
      return;
    }

    // Construct the JMuxer instance — it owns the <video>'s MediaSource.
    const muxer = new JMuxerCtor({
      node: videoRef.current,
      mode: 'video',           // video-only for now (audio via separate intercom WS later)
      // iter150.9 — was 0 (flush after every feed). With per-AU feeds,
      // flushingTime:0 created one fMP4 segment per AU. MSE rejects any
      // fMP4 segment that doesn't start with a keyframe → P-frame
      // segments were silently dropped, leaving only IDRs visible
      // (one every 2 s GOP on the MD300 sub-stream — exactly the
      // "video updates only in 2-sec or 4-sec jumps" pattern).
      // 2000 ms > the 2 s IDR interval, so every flushed segment is
      // guaranteed to contain at least one IDR + the following P-frames
      // in one keyframe-aligned chunk. Latency cost: up to 2 s of
      // pre-roll; the user is monitoring driver behaviour, not driving
      // a car, so 2 s is acceptable.
      flushingTime: 2000,
      fps: 25,                 // hint for any feed without explicit duration
      clearBuffer: true,       // 5-s back-buffer cap; fine for live monitoring
      onReady: () => { reconnectAttemptsRef.current = 0; },
      onError: (err) => {

        console.warn('[NativeLivePlayer] jmuxer error:', err?.message || err);
      },
    });
    muxerRef.current = muxer;

    // Open the WebSocket.
    let ws;
    try {
      ws = new WebSocket(targetUrl);
      ws.binaryType = 'arraybuffer';
    } catch (err) {

      console.warn('[NativeLivePlayer] ws open threw:', err?.message);
      scheduleReconnect();
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectDelayRef.current = 1000; // reset back-off
      updateStatus('connecting');
      recordDebugEvent('ws.open');
    };

    ws.onmessage = (event) => {
      // JSON control frame (ready / error) arrives as text; binary frames
      // are H.264 Annex-B payload bytes from the listener.
      if (typeof event.data === 'string') {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'ready') {
            updateStatus('connecting'); // first NAL bytes will flip to 'playing'
            recordDebugEvent('ws.ready');
          } else if (msg.type === 'error') {
            updateStatus('retrying');
            // No active session yet — schedule a quick re-poll.
            recordDebugEvent('ws.error-msg');
            scheduleReconnect();
          }
        } catch (_) { /* noop */ }
        return;
      }

      // Binary frame — the native-live WS wraps each access unit in a 32-byte
      // ELV1 envelope. JMuxer is H.264-only, so strip the envelope and skip
      // audio / HEVC payloads (those are simply ignored on this fallback path).
      const annexb = extractH264Annexb(event.data);
      if (!annexb || annexb.length === 0) return;
      if (debugMode) {
        debugCountersRef.current.wsFrames += 1;
        debugCountersRef.current.wsBytes += annexb.length;
      }
      try {
        muxer.feed({ video: annexb });
      } catch (err) {

        console.warn('[NativeLivePlayer] feed error:', err?.message);
      }
    };

    ws.onerror = () => {
      updateStatus('retrying');
      recordDebugEvent('ws.onerror');
    };

    ws.onclose = (ev) => {
      recordDebugEvent(`ws.close(${ev?.code})`);
      if (closedByUnmountRef.current) return;
      // 4404 = backend told us no active live session yet.
      // 4401 = auth — won't fix itself; surface as error.
      if (ev?.code === 4401) {
        updateStatus('error');
        return;
      }
      scheduleReconnect();
    };
  // `connect` and `scheduleReconnect` are mutually-recursive useCallbacks (the latter
  // is declared just below). Listing scheduleReconnect here would regenerate both on
  // every render and churn the WebSocket effect; reconnect is wired imperatively on
  // purpose.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsUrl, wsUrlFactory, teardown, updateStatus, debugMode, recordDebugEvent]);

  const scheduleReconnect = useCallback(() => {
    if (closedByUnmountRef.current) return;
    // iter150.3 — give up after MAX_RECONNECT_ATTEMPTS instead of flashing
    // forever. The user can click the channel button again to retry.
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {

      console.warn('[NativeLivePlayer] giving up after',
        reconnectAttemptsRef.current, 'reconnect attempts');
      updateStatus('error');
      return;
    }
    reconnectAttemptsRef.current += 1;
    updateStatus('retrying');
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    const delay = reconnectDelayRef.current;
    reconnectTimerRef.current = setTimeout(connect, delay);
    reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);
  }, [connect, updateStatus]);

  useEffect(() => {
    closedByUnmountRef.current = false;
    reconnectDelayRef.current = 1000;
    reconnectAttemptsRef.current = 0;
    connect();
    return () => {
      closedByUnmountRef.current = true;
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsUrl, wsUrlFactory]);

  // iter150.6 — poll the <video> element + WS state every 500 ms while
  // debug mode is active so the overlay reflects what's actually
  // happening inside the browser.
  useEffect(() => {
    if (!debugMode) return undefined;
    const tick = () => {
      const v = videoRef.current;
      const ws = wsRef.current;
      const wsStateMap = ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'];
      const buf = v?.buffered;
      let bufferedSummary = 'none';
      if (buf && buf.length > 0) {
        const ranges = [];
        for (let i = 0; i < buf.length; i += 1) {
          ranges.push(`${buf.start(i).toFixed(2)}-${buf.end(i).toFixed(2)}`);
        }
        bufferedSummary = ranges.join(',');
      }
      setDebugSnapshot({
        ws: ws ? wsStateMap[ws.readyState] : '—',
        wsFrames: debugCountersRef.current.wsFrames,
        wsBytes: debugCountersRef.current.wsBytes,
        retries: reconnectAttemptsRef.current,
        videoReady: v?.readyState ?? '—',
        videoPaused: v ? String(v.paused) : '—',
        videoCurrentTime: v ? v.currentTime.toFixed(2) : '—',
        videoBuffered: bufferedSummary,
        events: [...debugCountersRef.current.events],
        status,
      });
    };
    tick();
    const i = setInterval(tick, 500);
    return () => clearInterval(i);
  }, [debugMode, status]);

  React.useImperativeHandle(ref, () => ({
    setMuted: (muted) => {
      if (videoRef.current) {
        videoRef.current.muted = muted;
        if (!muted) videoRef.current.play().catch(() => { /* autoplay quirk */ });
      }
    },
    goFullscreen: () => {
      const el = containerRef.current;
      if (!el) return;
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    },
  }));

  return (
    <div ref={containerRef} className="relative w-full h-full bg-black" data-testid="native-live-player">
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        muted
        playsInline
        autoPlay
        data-testid="native-live-video"
        // iter150.6 — wire native <video> events:
        //   • onPlaying / onLoadedData / onCanPlay → flip to 'playing'
        //     and reset reconnect counter.
        //   • all other events are recorded for the debug overlay only;
        //     they NEVER drive React state on their own (iter150.5's
        //     `onWaiting → status='connecting'` was making the overlay
        //     stick because MSE constantly underruns by ≤1 frame and
        //     fires `waiting` even during normal playback).
        onPlaying={() => {
          reconnectAttemptsRef.current = 0;
          updateStatus('playing');
          recordDebugEvent('vid.playing');
        }}
        onLoadedData={() => {
          reconnectAttemptsRef.current = 0;
          updateStatus('playing');
          recordDebugEvent('vid.loadeddata');
        }}
        onCanPlay={() => {
          reconnectAttemptsRef.current = 0;
          updateStatus('playing');
          recordDebugEvent('vid.canplay');
        }}
        onWaiting={() => recordDebugEvent('vid.waiting')}
        onStalled={() => recordDebugEvent('vid.stalled')}
        onError={(e) => recordDebugEvent(`vid.error(${e?.target?.error?.code ?? '?'})`)}
        onPause={() => recordDebugEvent('vid.pause')}
      />
      {status === 'connecting' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 pointer-events-none">
          <div className="text-center text-white">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p className="text-sm">Connecting{label ? ` · ${label}` : ''}…</p>
          </div>
        </div>
      )}
      {status === 'retrying' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 pointer-events-none">
          <div className="text-center text-white">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p className="text-sm text-amber-400">Waiting for device stream…</p>
          </div>
        </div>
      )}
      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/70 pointer-events-none">
          <div className="text-center text-white px-4">
            <p className="text-sm text-red-400 font-medium">Stream lost</p>
            <p className="text-xs text-white/60 mt-1">
              Device didn't deliver video — click the channel button again to retry
            </p>
          </div>
        </div>
      )}

      {/* iter150.6 — Debug overlay. Enabled only with `?debug=1` in the URL.
          Shows the live truth of what WS / JMuxer / <video> are actually
          doing so we can stop guessing. Position bottom-left to avoid
          clobbering the channel/CONNECTING badges. */}
      {debugMode && debugSnapshot && (
        <div
          className="absolute bottom-2 left-2 z-30 bg-black/80 text-[10px] text-green-400 font-mono px-2 py-1 rounded leading-tight max-w-[60%] whitespace-pre"
          data-testid="native-live-debug"
        >
          {`status:    ${debugSnapshot.status}\n`}
          {`ws:        ${debugSnapshot.ws}  retries=${debugSnapshot.retries}\n`}
          {`ws-recv:   frames=${debugSnapshot.wsFrames}  bytes=${debugSnapshot.wsBytes}\n`}
          {`video:     readyState=${debugSnapshot.videoReady}  paused=${debugSnapshot.videoPaused}\n`}
          {`video.t:   ${debugSnapshot.videoCurrentTime}s  buffered=${debugSnapshot.videoBuffered}\n`}
          {`events:    ${debugSnapshot.events.join(' | ')}`}
        </div>
      )}
    </div>
  );
});

export default NativeLivePlayer;
